import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Coins, Play, StopCircle, ArrowsClockwise, Gauge, Globe, Buildings,
  CheckCircle, WarningCircle, Rocket, ShieldCheck,
} from "@phosphor-icons/react";

const nf = (n) => (n ?? 0).toLocaleString("fr-FR");
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}
const STATUS_STYLE = {
  running: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  finished: "text-accent border-accent/40 bg-accent/10",
  stopped: "text-fg/70 border-border bg-surface-2",
  error: "text-destructive border-destructive/40 bg-destructive/10",
  idle: "text-muted border-border bg-surface-2",
};

function Stat({ label, value, testid }) {
  return (
    <div className="bg-surface-2 border border-border rounded-md px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
      <div className="text-sm font-mono font-semibold text-fg mt-0.5">{value}</div>
    </div>
  );
}

function Bars({ title, rows, testid }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold text-fg mb-2">{title}</div>
      <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
        {(rows || []).length === 0 && <span className="text-xs text-muted">—</span>}
        {(rows || []).map((r) => (
          <div key={r.key} className="flex items-center gap-2 text-[11px]">
            <span className="w-32 shrink-0 truncate text-muted" title={r.key}>{r.key}</span>
            <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden">
              <div className="h-full bg-accent/60 rounded-full" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <span className="w-12 text-right font-mono text-fg/80">{nf(r.count)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BackfillCampaign() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("130000");
  const [reserve, setReserve] = useState("12000");
  const [frRatio, setFrRatio] = useState("75");
  const [deadline, setDeadline] = useState("2026-09-08T20:00:00Z");
  const [testCredits, setTestCredits] = useState("150");
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/collection/backfill/report");
      setReport(data);
    } catch (e) { /* silent during polling */ }
  }, []);

  useEffect(() => {
    load();
    poll.current = setInterval(load, 6000);
    return () => clearInterval(poll.current);
  }, [load]);

  const start = async (testMode) => {
    setBusy(true);
    try {
      const body = {
        target_credits: Number(target) || 130000,
        reserve_floor: Number(reserve) || 12000,
        fr_ratio: (Number(frRatio) || 75) / 100,
        deadline,
      };
      if (testMode) body.test_credits = Number(testCredits) || 150;
      const { data } = await api.post("/admin/collection/backfill/start", body);
      if (data.error) toast.error(data.error);
      else toast.success(testMode
        ? `Mini-lot de test lancé (~${nf(Number(testCredits))} crédits)`
        : "Campagne de backfill lancée");
      setTimeout(load, 1500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/admin/collection/backfill/stop");
      toast.success("Arrêt demandé — la campagne s'arrêtera à la fin du lot en cours.");
      setTimeout(load, 1500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const bal = report?.live_balance || {};
  const st = report?.status || "idle";
  const running = st === "running";
  const stats = report?.stats || {};
  const bd = report?.breakdown || {};
  const pct = report?.params?.target_credits
    ? Math.min(100, Math.round(((report?.credits_consumed_real || 0) / report.params.target_credits) * 100))
    : 0;

  return (
    <div className="surface rounded-lg p-6 mb-6" data-testid="backfill-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
            <Rocket size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg flex items-center gap-2">
              Campagne de backfill HasData
              <span className="text-[10px] font-mono text-muted border border-border rounded-full px-2 py-0.5">FR 75% · US 25% · Indeed</span>
            </h3>
            <p className="text-xs text-muted">Dépense contrôlée et unique de crédits — signaux IT récents (7→30→60→90 j), dédup globale, arrêt auto sur réserve/cible/deadline.</p>
          </div>
        </div>
        <span className={`flex items-center gap-1 text-xs font-mono border rounded-full px-2 py-1 ${STATUS_STYLE[st]}`} data-testid="backfill-status">
          {running ? <ArrowsClockwise size={12} className="animate-spin" /> : st === "error" ? <WarningCircle size={12} weight="fill" /> : <CheckCircle size={12} weight="fill" />} {st}
        </span>
      </div>

      {/* Live balance */}
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-surface-2 border border-accent/30 rounded-md px-3 py-2" data-testid="backfill-balance">
          <div className="text-[10px] uppercase tracking-wide text-muted flex items-center gap-1"><Coins size={11} /> Solde HasData (live)</div>
          <div className="text-lg font-mono font-bold text-accent mt-0.5">{bal.error ? "—" : nf(bal.available)}</div>
          <div className="text-[10px] text-muted">/ {nf(bal.total)} · concurrence {nf(bal.available_concurrency)}</div>
        </div>
        <Stat label="Consommé (réel)" value={nf(report?.credits_consumed_real)} testid="backfill-consumed-real" />
        <Stat label="Consommé (estimé)" value={nf(report?.credits_consumed_est)} testid="backfill-consumed-est" />
        <Stat label="Requêtes" value={nf(report?.requests_done)} testid="backfill-requests" />
      </div>

      {/* Progress bar */}
      {(running || report?.credits_consumed_real > 0) && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-[11px] text-muted mb-1">
            <span>Progression vers la cible ({nf(report?.params?.target_credits)} crédits)</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="h-2.5 bg-surface-2 rounded-full overflow-hidden">
            <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="backfill-progress" />
          </div>
          {report?.stop_reason && <p className="text-[11px] text-muted mt-1">Dernier arrêt : {report.stop_reason}</p>}
        </div>
      )}

      {/* Controls */}
      <div className="grid sm:grid-cols-5 gap-3 border-t border-border pt-4 mb-3">
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Gauge size={12} /> Cible crédits</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={target} onChange={(e) => setTarget(e.target.value)} disabled={running} data-testid="backfill-target" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><ShieldCheck size={12} /> Réserve (plancher)</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={reserve} onChange={(e) => setReserve(e.target.value)} disabled={running} data-testid="backfill-reserve" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Globe size={12} /> Part France (%)</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={frRatio} onChange={(e) => setFrRatio(e.target.value)} disabled={running} data-testid="backfill-fr-ratio" />
        </div>
        <div>
          <label className="overline block mb-1.5">Deadline (UTC)</label>
          <Input className="bg-surface-2 border-border text-xs h-9" value={deadline} onChange={(e) => setDeadline(e.target.value)} disabled={running} data-testid="backfill-deadline" />
        </div>
        <div>
          <label className="overline block mb-1.5">Test (crédits)</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={testCredits} onChange={(e) => setTestCredits(e.target.value)} disabled={running} data-testid="backfill-test-credits" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {!running ? (
          <>
            <button onClick={() => start(true)} disabled={busy || bal.error} data-testid="backfill-start-test"
              className="flex items-center gap-2 bg-surface-2 border border-accent/40 text-accent font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9 text-sm">
              <Play size={15} weight="fill" /> Lancer le mini-lot de test
            </button>
            <button onClick={() => start(false)} disabled={busy || bal.error} data-testid="backfill-start-full"
              className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9 text-sm">
              <Rocket size={15} weight="fill" /> Lancer la campagne complète
            </button>
          </>
        ) : (
          <button onClick={stop} disabled={busy} data-testid="backfill-stop"
            className="flex items-center gap-2 bg-destructive/15 border border-destructive/40 text-destructive font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9 text-sm">
            <StopCircle size={15} weight="fill" /> Arrêter la campagne
          </button>
        )}
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-muted hover:text-accent px-3 h-9">
          <ArrowsClockwise size={13} /> Rafraîchir
        </button>
      </div>

      {/* Import stats */}
      <div className="grid sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
        <Stat label="Offres reçues" value={nf(stats.records_received)} testid="backfill-received" />
        <Stat label="Opportunités créées" value={nf(stats.created)} testid="backfill-created" />
        <Stat label="Mises à jour" value={nf(stats.updated)} testid="backfill-updated" />
        <Stat label="Doublons rejetés" value={nf(stats.duplicates)} testid="backfill-duplicates" />
        <Stat label="Entreprises créées" value={nf(stats.companies_created)} testid="backfill-companies" />
        <Stat label="ESN / intermédiaires" value={nf(stats.intermediaries)} testid="backfill-intermediaries" />
        <Stat label="Périmées (>90j)" value={nf(stats.stale_dropped)} testid="backfill-stale" />
        <Stat label="Intérim exclus" value={nf(stats.staffing_dropped)} testid="backfill-staffing" />
        <Stat label="Signaux en base" value={nf(report?.signals_in_db)} testid="backfill-signals-db" />
        <Stat label="Entreprises touchées" value={nf(report?.companies_touched)} testid="backfill-companies-touched" />
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-2 gap-6 border-t border-border pt-4 mb-4">
        <Bars title="Par pays" rows={bd.country} testid="backfill-bd-country" />
        <Bars title="Par type de client" rows={bd.client_type} testid="backfill-bd-client" />
        <Bars title="Par famille de métier" rows={bd.job_family} testid="backfill-bd-family" />
        <Bars title="Par ville" rows={bd.city} testid="backfill-bd-city" />
      </div>

      {/* Batches */}
      <div className="border-t border-border pt-4">
        <div className="text-xs font-semibold text-fg mb-2">Lots (solde avant/après · crédits réels)</div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="backfill-batches">
          {(report?.batches || []).length === 0 && <span className="text-xs text-muted">Aucun lot pour l'instant.</span>}
          {(report?.batches || []).slice().reverse().map((b) => (
            <div key={b.idx} className="text-[11px] bg-surface-2 border border-border rounded-md px-3 py-1.5 flex items-center gap-3 flex-wrap font-mono">
              <span className="text-accent">Lot #{b.idx}</span>
              <span className="text-muted">{fmtDate(b.at)}</span>
              <span className="text-fg/80">{b.requests} req</span>
              <span className="text-fg/80">{nf(b.balance_before)} → {nf(b.balance_after)}</span>
              <span className="text-amber-400">-{nf(b.credits_real)} cr</span>
            </div>
          ))}
        </div>
      </div>

      {/* Errors */}
      {(report?.errors || []).length > 0 && (
        <div className="mt-4 space-y-1" data-testid="backfill-errors">
          {report.errors.map((e, i) => (
            <div key={i} className="text-[11px] bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-3 py-1.5">
              {fmtDate(e.at)} — {e.message}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted mt-4 flex items-center gap-1.5">
        <ShieldCheck size={12} /> Garde-fous : arrêt auto si solde ≤ réserve, si la cible est atteinte, ou à la deadline. Le solde live HasData fait foi. La collecte récurrente normale continue de tourner en parallèle.
      </p>
    </div>
  );
}
